import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { billingAPI } from '../../services/api';
import TransferOwnershipCard from './TransferOwnershipCard';

const COMPONENT_LABELS = {
    observation_flat: 'Observation',
    scenario_flat: 'Scenario Run',
    llm_metric: 'LLM Metric',
    non_llm_metric: 'Non-LLM Metric',
    manual_credit: 'Manual Credit',
    plan_credit: 'Plan Credit',
};

const TYPE_BADGE = {
    credit: 'bg-green-100 text-green-800',
    debit: 'bg-red-100 text-red-800',
};

const BillingSettings = () => {
    const { currentWorkspace } = useAuth();
    const [summary, setSummary] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, observation_flat, scenario_flat, llm_metric, non_llm_metric

    const fetchData = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        setLoading(true);
        try {
            const [summaryRes, txnRes] = await Promise.all([
                billingAPI.getSummary(currentWorkspace.id),
                billingAPI.getTransactions({
                    page,
                    per_page: 20,
                    ...(filter !== 'all' ? { component: filter } : {}),
                }),
            ]);
            setSummary(summaryRes.data);
            setTransactions(txnRes.data || []);
            setPagination(txnRes.pagination || {});
        } catch (err) {
            console.error('Failed to fetch billing data:', err);
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id, page, filter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const formatCredits = (val) => {
        if (val == null) return '0.00';
        return Number(val).toFixed(2);
    };

    const breakdown = summary?.breakdown || {};

    return (
        <div className="space-y-6">
            {/* Credit Balance Cards */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-card rounded-lg border border-border p-4">
                    <p className="text-sm text-muted-foreground mb-1">Credits Remaining</p>
                    <p className="text-3xl font-semibold text-foreground">{formatCredits(summary?.balance)}</p>
                </div>
                <div className="bg-card rounded-lg border border-border p-4">
                    <p className="text-sm text-muted-foreground mb-1">Total Credits Used</p>
                    <p className="text-3xl font-semibold text-foreground">{formatCredits(summary?.total_credits_used)}</p>
                </div>
            </div>

            {/* Usage Breakdown */}
            <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4">Usage Breakdown</h3>
                <div className="grid grid-cols-4 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-1">Observations</p>
                        <p className="text-xl font-semibold text-foreground">{formatCredits(breakdown.observation_flat)}</p>
                        {summary?.pricing?.observation_flat != null && (
                            <p className="text-xs text-muted-foreground mt-1">{summary.pricing.observation_flat} credits/observation</p>
                        )}
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-1">Scenario Runs</p>
                        <p className="text-xl font-semibold text-foreground">{formatCredits(breakdown.scenario_flat)}</p>
                        {summary?.pricing?.scenario_flat != null && (
                            <p className="text-xs text-muted-foreground mt-1">{summary.pricing.scenario_flat} credits/run</p>
                        )}
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-1">LLM Metrics</p>
                        <p className="text-xl font-semibold text-foreground">{formatCredits(breakdown.llm_metric)}</p>
                        {summary?.pricing?.llm_metric != null && (
                            <p className="text-xs text-muted-foreground mt-1">{summary.pricing.llm_metric} credits/metric</p>
                        )}
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-1">Non-LLM Metrics</p>
                        <p className="text-xl font-semibold text-foreground">{formatCredits(breakdown.non_llm_metric)}</p>
                        {summary?.pricing?.non_llm_metric != null && (
                            <p className="text-xs text-muted-foreground mt-1">{summary.pricing.non_llm_metric} credits/metric</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Transaction History */}
            <div className="bg-card rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Transaction History</h3>
                    <select
                        value={filter}
                        onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                        className="text-sm rounded-md border-input shadow-sm focus:border-black focus:ring-black"
                    >
                        <option value="all">All</option>
                        <option value="observation_flat">Observations</option>
                        <option value="scenario_flat">Scenario Runs</option>
                        <option value="llm_metric">LLM Metrics</option>
                        <option value="non_llm_metric">Non-LLM Metrics</option>
                        <option value="manual_credit">Manual Credits</option>
                    </select>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Timestamp</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-muted-foreground">Loading...</td>
                                </tr>
                            ) : transactions.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-muted-foreground">No transactions yet</td>
                                </tr>
                            ) : (
                                transactions.map((txn) => (
                                    <tr key={txn.id}>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                                            {new Date(txn.timestamp).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${TYPE_BADGE[txn.transaction_type]}`}>
                                                {txn.transaction_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-foreground">
                                            <div>{txn.description}</div>
                                            {txn.client && (
                                                <div className="text-xs text-muted-foreground mt-0.5">{txn.client.name}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                            <span className={txn.transaction_type === 'debit' ? 'text-red-600' : 'text-green-600'}>
                                                {txn.transaction_type === 'debit' ? '-' : '+'}{formatCredits(txn.amount)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="px-6 py-3 border-t border-border flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                            Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} transactions)
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 text-sm rounded border border-input hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                                disabled={page === pagination.total_pages}
                                className="px-3 py-1 text-sm rounded border border-input hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <TransferOwnershipCard />
        </div>
    );
};

export default BillingSettings;
